import { useForm } from 'react-hook-form';
import { register, googleAuth } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import GoogleAuthButton from '../components/GoogleAuthButton';
import LanguageSelector from '../components/LanguageSelector';
import { GoogleUser } from '../services/googleAuthService';

interface FormValues { 
  email: string; 
  password: string; 
  confirmPassword: string;
}

export default function Register() {
  const { register: registerField, handleSubmit, formState: { errors, isSubmitting }, watch } = useForm<FormValues>();
  const { login: saveAuth } = useAuth();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  
  const password = watch('password');

  const onSubmit = async (values: FormValues) => {
    try {
      await register({ email: values.email, password: values.password });
      window.alert(t('auth.registrationSuccess'));
      navigate('/login');
    } catch (e) {
      window.alert(t('auth.error'));
    }
  };

  const handleGoogleAuth = async (user: GoogleUser) => {
    try {
      // Получаем токен от Google и отправляем его на бэкенд
      const res = await googleAuth({ token: user.token });
      saveAuth(res.token, res.user);
      window.alert(`Добро пожаловать, ${user.email}!`);
      navigate('/');
    } catch (e) {
      window.alert(t('auth.error'));
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Language selector in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector 
          selectedLanguage={language}
          onLanguageChange={setLanguage}
        />
      </div>
      
      <div className="min-h-screen grid place-items-center px-6">
        <div className="w-full max-w-sm bg-white p-6 rounded-2xl border border-gray-100">
        <div className="flex items-center justify-center mb-6">
          <img 
            src="/logo.png" 
            alt="FAKTURAMA" 
            className="h-16 w-16 object-contain"
          />
        </div>
        <h1 className="text-2xl font-bold mb-6 text-center">{t('auth.register')}</h1>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('auth.email')}</label>
            <input 
              className="w-full rounded-xl border border-gray-200 px-3 py-2" 
              type="email" 
              {...registerField('email', { 
                required: t('auth.emailRequired'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Введите корректный email'
                }
              })} 
            />
            {errors.email && <div className="text-sm text-red-600 mt-1">{errors.email.message}</div>}
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('auth.password')}</label>
            <input 
              className="w-full rounded-xl border border-gray-200 px-3 py-2" 
              type="password" 
              {...registerField('password', { 
                required: t('auth.passwordRequired'),
                minLength: {
                  value: 6,
                  message: t('auth.passwordMinLength')
                }
              })} 
            />
            {errors.password && <div className="text-sm text-red-600 mt-1">{errors.password.message}</div>}
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('auth.confirmPassword')}</label>
            <input 
              className="w-full rounded-xl border border-gray-200 px-3 py-2" 
              type="password" 
              {...registerField('confirmPassword', { 
                required: t('auth.passwordRequired'),
                validate: value => value === password || t('auth.passwordsDoNotMatch')
              })} 
            />
            {errors.confirmPassword && <div className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</div>}
          </div>
          
          <button 
            disabled={isSubmitting} 
            className="w-full rounded-xl bg-blue-600 text-white py-2 disabled:opacity-60"
          >
            {t('auth.registerButton')}
          </button>
        </form>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Или</span>
            </div>
          </div>
          
          <div className="mt-4">
            <GoogleAuthButton 
              onSuccess={handleGoogleAuth}
              onError={(error) => window.alert(error)}
            />
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-800">
              {t('auth.switchToLogin')}
            </Link>
          </p>
          </div>
        </div>
      </div>
    </div>
  );
}
